import { DragHandleIcon } from '@chakra-ui/icons';
import { Box, Icon, Td, Tr } from '@chakra-ui/react';
import type { Identifier, XYCoord } from 'dnd-core';
import React, { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';

import * as types from '../types';

export const TrDraggableHandle: React.FC<types.TrDraggableHandle.Props> = ({
  acceptedType,
  id,
  index,
  isDragDisabled = false,
  moveRow,
  children,
  draggableIconProps,
}) => {
  const dragRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLTableRowElement>(null);
  const [{ handlerId }, drop] = useDrop<
    types.DragItem,
    void,
    { handlerId: Identifier | null }
  >({
    accept: acceptedType,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: types.DragItem, monitor) {
      if (!dragRef.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = dragRef.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      moveRow(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      // eslint-disable-next-line no-param-reassign
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: acceptedType,
    canDrag: !isDragDisabled,
    item: () => {
      return { id, index };
    },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0 : 1;
  drag(dragRef);
  drop(preview(previewRef));
  return (
    <Tr
      style={{
        opacity,
        cursor: isDragging ? 'grabbing' : 'default',
      }}
      ref={previewRef}
      data-handler-id={handlerId}
    >
      <Td
        display={!isDragDisabled ? undefined : 'none'}
        {...draggableIconProps}
      >
        <Box
          ref={dragRef}
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          height="20px"
        >
          <Icon display="absolute" float="left">
            <DragHandleIcon />
          </Icon>
        </Box>
      </Td>
      {children}
    </Tr>
  );
};
